import { useCallback, useState, useEffect } from 'react';
import { getToken, deleteToken } from 'firebase/messaging';
import { getDoc, doc, setDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db, getMessagingOrNull, messagingReady } from '../../providers/firebase/firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

interface UseFcmTokenResult {
    permission: NotificationPermission;
    enabledOnThisDevice: boolean | undefined; // undefined while resolving
    currentToken: string | null;
    ensureToken: () => Promise<string | null>;
    revokeToken: () => Promise<void>;
}

// The SW loads its Firebase config from /firebase-messaging-config.js (emitted
// at build time), so no config needs to be passed via the registration URL.
const SW_URL = '/firebase-messaging-sw.js';

export function useFcmToken(): UseFcmTokenResult {
    const [permission, setPermission] = useState<NotificationPermission>(
        typeof Notification !== 'undefined' ? Notification.permission : 'default'
    );
    const [enabledOnThisDevice, setEnabledOnThisDevice] = useState<boolean | undefined>(undefined);
    const [currentToken, setCurrentToken] = useState<string | null>(null);

    useEffect(() => {
        if (typeof Notification === 'undefined') return;
        setPermission(Notification.permission);
    }, []);

    useEffect(() => {
        const auth = getAuth();
        let cancelled = false;
        const resolve = async (uid: string | undefined) => {
            if (!VAPID_KEY || !uid || typeof Notification === 'undefined' || Notification.permission !== 'granted') {
                if (!cancelled) { setEnabledOnThisDevice(false); setCurrentToken(null); }
                return;
            }
            try {
                const messaging = await messagingReady;
                if (cancelled) return;
                if (!messaging) { setEnabledOnThisDevice(false); setCurrentToken(null); return; }
                const swReg = await navigator.serviceWorker.register(SW_URL);
                await navigator.serviceWorker.ready;
                const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
                const snap = await getDoc(doc(db, `users/${uid}/notification_settings/main`));
                const tokens = (snap.data()?.fcm_tokens ?? {}) as Record<string, unknown>;
                if (!cancelled) { setCurrentToken(token ?? null); setEnabledOnThisDevice(!!token && token in tokens); }
            } catch {
                if (!cancelled) { setEnabledOnThisDevice(false); setCurrentToken(null); }
            }
        };
        resolve(auth.currentUser?.uid ?? undefined);
        const unsub = onAuthStateChanged(auth, u => resolve(u?.uid ?? undefined));
        return () => { cancelled = true; unsub(); };
    }, [permission]);

    const ensureToken = useCallback(async (): Promise<string | null> => {
        const messaging = getMessagingOrNull();
        if (!messaging || !VAPID_KEY) return null;
        const uid = getAuth().currentUser?.uid;
        if (!uid) return null;

        if (Notification.permission === 'default') {
            const result = await Notification.requestPermission();
            setPermission(result);
            if (result !== 'granted') return null;
        }
        if (Notification.permission !== 'granted') return null;

        const swReg = await navigator.serviceWorker.register(SW_URL);
        // Wait for an active worker before getToken — PushManager.subscribe
        // fails with "no active Service Worker" if the registration hasn't
        // activated yet.
        await navigator.serviceWorker.ready;
        const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
        if (!token) return null;

        await setDoc(
            doc(db, `users/${uid}/notification_settings/main`),
            { fcm_tokens: { [token]: { created_at: serverTimestamp(), user_agent: navigator.userAgent } } },
            { merge: true },
        );
        setCurrentToken(token);
        setEnabledOnThisDevice(true);
        return token;
    }, []);

    const revokeToken = useCallback(async (): Promise<void> => {
        const messaging = getMessagingOrNull();
        if (!messaging || !VAPID_KEY) return;
        const uid = getAuth().currentUser?.uid;
        if (!uid) return;
        try {
            // Use the SAME service worker registration ensureToken used, so
            // getToken returns the token that was actually stored. Without an
            // explicit registration the SDK falls back to its own default SW
            // (a different scope/subscription), yielding a different token —
            // so the stored one would never get deleted.
            const swReg = await navigator.serviceWorker.register(SW_URL);
            await navigator.serviceWorker.ready;
            const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
            if (token) {
                await setDoc(
                    doc(db, `users/${uid}/notification_settings/main`),
                    { fcm_tokens: { [token]: deleteField() } },
                    { merge: true },
                );
                await deleteToken(messaging);
            }
            setCurrentToken(null);
            setEnabledOnThisDevice(false);
        } catch (e) {
            console.warn('revokeToken: no active FCM token to revoke', e);
        }
    }, []);

    return { permission, enabledOnThisDevice, currentToken, ensureToken, revokeToken };
}
